import type { Edge, Node } from '@xyflow/react';
import { MarkerType } from '@xyflow/react';

import type { Recipe } from '@/types';
import { logger } from '@/utils/logger';

// Layout constants
const LAYER_DISTANCE = 250;
const NODE_DISTANCE = 150;
const START_X = 100;
const START_Y = 100;

// Reference types for edge styling
const REFERENCE_TYPES = [
  '@ref-connector-id',
  'ref-connector-id',
  '@ref-statement-id',
  'ref-statement-id',
  '@ref-dataframe-id',
  'ref-dataframe-id',
  '@ref-task-id',
  'ref-task-id',
  '@ref-readFormat-id',
  'ref-readFormat-id',
  '@ref-writeFormat-id',
  'ref-writeFormat-id',
  '@ref-import-id',
  'ref-import-id',
] as const;

// Edge colors by reference type
const EDGE_COLORS: Record<string, string> = {
  connector: '#2196F3', // blue
  statement: '#F44336', // red
  dataframe: '#9C27B0', // purple
  task: '#FF9800', // orange
  readFormat: '#4CAF50', // green
  writeFormat: '#E65100', // dark orange
  import: '#9E9E9E', // gray
};

// Node category colors
const NODE_COLORS: Record<string, string> = {
  connector: '#4CAF50',
  readFormat: '#FF9800',
  writeFormat: '#E65100',
  statement: '#2196F3',
  task: '#F44336',
  import: '#9E9E9E',
};

/**
 * Parse ConfigJson string to get the recipe object
 */
export function parseConfigJson(configJson: string): Recipe | null {
  try {
    const parsed = JSON.parse(configJson);
    return parsed.recipe || parsed;
  } catch (error) {
    logger.error('Failed to parse ConfigJson:', error);
    return null;
  }
}

/**
 * Extract text value from XML-to-JSON objects or return the value as-is
 */
function extractTextValue(value: unknown): unknown {
  if (value === null || value === undefined) return value;

  // Handle object with #text property (XML-to-JSON artifact)
  if (typeof value === 'object' && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;

    // If it has #text, return that value
    if ('#text' in obj) {
      return obj['#text'];
    }

    // Otherwise, recursively clean the object
    const cleaned: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(obj)) {
      // Skip XML metadata keys
      if (!key.startsWith('@data-')) {
        cleaned[key] = extractTextValue(val);
      }
    }
    return cleaned;
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return value.map(extractTextValue);
  }

  return value;
}

/**
 * Get type string from node type property (handles object format)
 */
function getTypeString(type: string | { '#text': string } | undefined): string {
  if (!type) return 'unknown';
  if (typeof type === 'string') return type;
  if (typeof type === 'object' && '#text' in type) return type['#text'];
  return 'unknown';
}

/**
 * Get string value from property (handles object format)
 */
function getStringValue(value: string | { '#text': string } | undefined): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && '#text' in value) return value['#text'];
  return '';
}

/**
 * Ensure array format for recipe sections
 */
function ensureArray<T>(item: T | T[] | undefined): T[] {
  if (!item) return [];
  return Array.isArray(item) ? item : [item];
}

/**
 * Extract all references from a node object
 */
function extractReferences(node: Record<string, unknown>): Array<{ id: string; type: string }> {
  const refs: Array<{ id: string; type: string }> = [];

  for (const refType of REFERENCE_TYPES) {
    const refValue = node[refType];
    if (refValue && typeof refValue === 'string') {
      // References can be comma-separated
      const ids = refValue
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
      const type = refType.replace('@ref-', '').replace('ref-', '').replace('-id', '');
      ids.forEach((id) => refs.push({ id, type }));
    }
  }

  return refs;
}

/**
 * Convert recipe object to React Flow nodes and edges
 */
export function recipeToNodes(recipe: Recipe | null): { nodes: Node[]; edges: Edge[] } {
  if (!recipe) {
    return { nodes: [], edges: [] };
  }

  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const nodePositions = new Map<string, { x: number; y: number }>();
  const nodesByDepth = new Map<number, string[]>();

  // Track all node IDs for edge creation
  const allNodeIds = new Set<string>();

  // Helper to add a node
  const addNode = (
    id: string,
    type: string,
    nodeType: string,
    label: string,
    data: Record<string, unknown>,
    depth: number
  ) => {
    if (allNodeIds.has(id)) return; // Skip duplicates
    allNodeIds.add(id);

    // Track nodes by depth for layout
    if (!nodesByDepth.has(depth)) {
      nodesByDepth.set(depth, []);
    }
    nodesByDepth.get(depth)!.push(id);

    const nodesAtDepth = nodesByDepth.get(depth)!;
    const nodeIndex = nodesAtDepth.length - 1;

    const position = {
      x: START_X + depth * LAYER_DISTANCE,
      y: START_Y + nodeIndex * NODE_DISTANCE,
    };
    nodePositions.set(id, position);

    nodes.push({
      id,
      type: 'recipeNode', // Use our custom node type
      position,
      data: {
        '@id': id,
        type,
        nodeType,
        label,
        color: NODE_COLORS[nodeType] || '#666',
        ...data,
      },
    });
  };

  // Process Connectors (depth 0)
  const connectors = ensureArray(recipe.connectors?.connector);
  connectors.forEach((conn) => {
    const id = conn['@id'];
    const type = getTypeString(conn['@type'] || conn.type);
    const name = getStringValue(conn.name);
    const isWriter = type.toLowerCase().includes('writer');

    // Clean all properties to remove XML-to-JSON artifacts
    const cleanedConn = extractTextValue(conn) as Record<string, unknown>;

    addNode(
      id,
      type,
      'connector',
      name || type,
      {
        ...cleanedConn,
        name, // Ensure name is a string
        isOutput: isWriter,
        allReferences: extractReferences(conn as Record<string, unknown>),
      },
      isWriter ? 4 : 0 // Writers at the end, readers at the start
    );
  });

  // Process Read Formats (depth 1)
  const readFormats = ensureArray(recipe.readFormats?.readFormat);
  readFormats.forEach((fmt) => {
    const id = fmt['@id'];
    const type = getTypeString(fmt['@type'] || fmt.type);
    const name = getStringValue(fmt.name);

    // Clean all properties
    const cleanedFmt = extractTextValue(fmt) as Record<string, unknown>;

    addNode(
      id,
      `${type}Reader`,
      'readFormat',
      name || `${type} Reader`,
      {
        ...cleanedFmt,
        name,
        allReferences: extractReferences(fmt as Record<string, unknown>),
      },
      1
    );
  });

  // Process Statements (depth 2-3)
  const statements = ensureArray(recipe.statements?.statement);
  statements.forEach((stmt) => {
    const id = stmt['@id'];
    const dataType = stmt['@data-type'] || stmt['data-type'];
    const action = getStringValue(stmt.action) || 'default';
    const command = getStringValue(stmt.command);
    const query = getStringValue(stmt.query);
    const dataFrame = getStringValue(stmt['data-frame'] || stmt.dataFrame);

    let type: string;
    if (dataType === 'command') {
      type = `${action}Command`;
    } else if (dataType === 'dataframe') {
      type = action;
    } else {
      type = action || 'statement';
    }

    const label = command || query || dataFrame || action;

    // Clean all properties
    const cleanedStmt = extractTextValue(stmt) as Record<string, unknown>;

    addNode(
      id,
      type,
      'statement',
      label,
      {
        ...cleanedStmt,
        dataType,
        action,
        command,
        query,
        dataFrame,
        allReferences: extractReferences(stmt as Record<string, unknown>),
      },
      2
    );
  });

  // Process Write Formats (depth 3)
  const writeFormats = ensureArray(recipe.writeFormats?.writeFormat);
  writeFormats.forEach((fmt) => {
    const id = fmt['@id'];
    const type = getTypeString(fmt['@type'] || fmt.type);
    const name = getStringValue(fmt.name);

    // Clean all properties
    const cleanedFmt = extractTextValue(fmt) as Record<string, unknown>;

    addNode(
      id,
      `${type}Writer`,
      'writeFormat',
      name || `${type} Writer`,
      {
        ...cleanedFmt,
        name,
        allReferences: extractReferences(fmt as Record<string, unknown>),
      },
      3
    );
  });

  // Process Tasks (depth 2)
  const tasks = ensureArray(recipe.tasks?.task);
  tasks.forEach((task) => {
    const id = task['@id'];
    const type = getStringValue(task.type) || 'task';
    const func = getStringValue(task.function) || '';

    // Clean all properties
    const cleanedTask = extractTextValue(task) as Record<string, unknown>;

    addNode(
      id,
      `${type}${func}`,
      'task',
      func || type,
      {
        ...cleanedTask,
        type,
        function: func,
        allReferences: extractReferences(task as Record<string, unknown>),
      },
      2
    );
  });

  // Process Imports (depth 0)
  const imports = ensureArray(recipe.imports?.import);
  imports.forEach((imp) => {
    const id = imp['@id'];
    const type = getTypeString(imp['@type'] || imp.type);
    const path = getStringValue(imp.path);

    // Clean all properties
    const cleanedImp = extractTextValue(imp) as Record<string, unknown>;

    addNode(
      id,
      type,
      'import',
      path || type,
      {
        ...cleanedImp,
        path,
        allReferences: extractReferences(imp as Record<string, unknown>),
      },
      0
    );
  });

  // Create edges from references
  nodes.forEach((node) => {
    const refs = (node.data.allReferences as Array<{ id: string; type: string }>) || [];
    refs.forEach((ref) => {
      if (allNodeIds.has(ref.id)) {
        edges.push({
          id: `e-${ref.id}-${node.id}`,
          source: ref.id,
          target: node.id,
          animated: true,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 16,
            height: 16,
          },
          style: {
            stroke: EDGE_COLORS[ref.type] || '#999',
            strokeWidth: 2,
          },
          data: { refType: ref.type },
        });
      }
    });
  });

  return { nodes, edges };
}

/**
 * Convert React Flow nodes and edges back to recipe object
 */
export function nodesToRecipe(nodes: Node[], edges: Edge[]): Recipe {
  const recipe: Recipe = {};

  // Group nodes by type
  const connectorNodes = nodes.filter((n) => n.data.nodeType === 'connector');
  const readFormatNodes = nodes.filter((n) => n.data.nodeType === 'readFormat');
  const writeFormatNodes = nodes.filter((n) => n.data.nodeType === 'writeFormat');
  const statementNodes = nodes.filter((n) => n.data.nodeType === 'statement');
  const taskNodes = nodes.filter((n) => n.data.nodeType === 'task');
  const importNodes = nodes.filter((n) => n.data.nodeType === 'import');

  // Build edge map for reference reconstruction
  const edgesByTarget = new Map<string, Edge[]>();
  edges.forEach((edge) => {
    if (!edgesByTarget.has(edge.target)) {
      edgesByTarget.set(edge.target, []);
    }
    edgesByTarget.get(edge.target)!.push(edge);
  });

  // Helper to build reference attributes
  const buildRefs = (nodeId: string): Record<string, string> => {
    const refs: Record<string, string[]> = {};
    const nodeEdges = edgesByTarget.get(nodeId) || [];

    nodeEdges.forEach((edge) => {
      const sourceNode = nodes.find((n) => n.id === edge.source);
      if (sourceNode) {
        const refType = `@ref-${sourceNode.data.nodeType}-id`;
        if (!refs[refType]) refs[refType] = [];
        refs[refType].push(edge.source);
      }
    });

    const result: Record<string, string> = {};
    Object.entries(refs).forEach(([key, values]) => {
      result[key] = values.join(',');
    });
    return result;
  };

  // Build connectors
  if (connectorNodes.length > 0) {
    recipe.connectors = {
      connector: connectorNodes.map((n) => ({
        '@id': n.data['@id'] || n.id,
        '@type': n.data.type,
        ...buildRefs(n.id),
      })),
    };
  }

  // Build read formats
  if (readFormatNodes.length > 0) {
    recipe.readFormats = {
      readFormat: readFormatNodes.map((n) => ({
        '@id': n.data['@id'] || n.id,
        type: (n.data.type as string)?.replace('Reader', ''),
        ...buildRefs(n.id),
      })),
    };
  }

  // Build write formats
  if (writeFormatNodes.length > 0) {
    recipe.writeFormats = {
      writeFormat: writeFormatNodes.map((n) => ({
        '@id': n.data['@id'] || n.id,
        type: (n.data.type as string)?.replace('Writer', ''),
        ...buildRefs(n.id),
      })),
    };
  }

  // Build statements
  if (statementNodes.length > 0) {
    recipe.statements = {
      statement: statementNodes.map((n) => ({
        '@id': n.data['@id'] || n.id,
        '@data-type': n.data.dataType,
        action: n.data.action,
        'data-frame': n.data.dataFrame,
        command: n.data.command,
        query: n.data.query,
        ...buildRefs(n.id),
      })),
    };
  }

  // Build tasks
  if (taskNodes.length > 0) {
    recipe.tasks = {
      task: taskNodes.map((n) => ({
        '@id': n.data['@id'] || n.id,
        type: n.data.type,
        function: n.data.function,
        ...buildRefs(n.id),
      })),
    };
  }

  // Build imports
  if (importNodes.length > 0) {
    recipe.imports = {
      import: importNodes.map((n) => ({
        '@id': n.data['@id'] || n.id,
        type: n.data.type,
        path: n.data.path,
        ...buildRefs(n.id),
      })),
    };
  }

  return recipe;
}

/**
 * Convert recipe object to YAML string
 */
export function recipeToYaml(recipe: Recipe): string {
  return yamlStringify({ recipe }, 0);
}

/**
 * Convert YAML string to recipe object
 */
export function yamlToRecipe(yaml: string): Recipe {
  const parsed = yamlParse(yaml);
  return parsed.recipe || parsed;
}

/**
 * Simple YAML stringify implementation
 */
function yamlStringify(obj: unknown, indent = 0): string {
  const spaces = '  '.repeat(indent);

  if (obj === null || obj === undefined) {
    return 'null';
  }

  if (typeof obj === 'string') {
    if (obj.includes('\n') || obj.includes(':') || obj.includes('#') || obj.includes('"')) {
      return `"${obj.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n')}"`;
    }
    return obj;
  }

  if (typeof obj === 'number' || typeof obj === 'boolean') {
    return String(obj);
  }

  if (Array.isArray(obj)) {
    if (obj.length === 0) return '[]';
    return obj
      .map((item) => {
        const value = yamlStringify(item, indent + 1);
        if (typeof item === 'object' && item !== null) {
          const lines = value.split('\n');
          return `${spaces}- ${lines[0].trim()}\n${lines
            .slice(1)
            .map((l) => `${spaces}  ${l.trim()}`)
            .join('\n')}`.trim();
        }
        return `${spaces}- ${value}`;
      })
      .join('\n');
  }

  if (typeof obj === 'object') {
    const entries = Object.entries(obj).filter(([_, v]) => v !== undefined);
    if (entries.length === 0) return '{}';

    return entries
      .map(([key, value]) => {
        const yamlValue = yamlStringify(value, indent + 1);
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          return `${spaces}${key}:\n${yamlValue}`;
        }
        if (Array.isArray(value)) {
          return `${spaces}${key}:\n${yamlValue}`;
        }
        return `${spaces}${key}: ${yamlValue}`;
      })
      .join('\n');
  }

  return String(obj);
}

/**
 * Simple YAML parse implementation
 */
function yamlParse(yaml: string): Record<string, unknown> {
  const lines = yaml.split('\n');
  const result: Record<string, unknown> = {};
  const stack: Array<{ obj: Record<string, unknown>; indent: number; key?: string }> = [
    { obj: result, indent: -2 },
  ];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim() || line.trim().startsWith('#')) continue;

    const indent = line.search(/\S/);
    const content = line.trim();

    // Pop stack to correct level
    while (stack.length > 1 && stack[stack.length - 1].indent >= indent) {
      stack.pop();
    }

    const current = stack[stack.length - 1].obj;

    // Array item
    if (content.startsWith('- ')) {
      const parentKey = stack[stack.length - 1].key;
      if (parentKey && !Array.isArray(current[parentKey])) {
        current[parentKey] = [];
      }
      const arr = parentKey ? (current[parentKey] as unknown[]) : [];

      const value = content.slice(2).trim();
      if (value.includes(':')) {
        const colonIdx = value.indexOf(':');
        const key = value.slice(0, colonIdx).trim();
        const val = value.slice(colonIdx + 1).trim();
        const newObj: Record<string, unknown> = {};
        newObj[key] = parseValue(val);
        arr.push(newObj);
        stack.push({ obj: newObj, indent, key: undefined });
      } else {
        arr.push(parseValue(value));
      }
      continue;
    }

    // Key-value
    const colonIdx = content.indexOf(':');
    if (colonIdx > 0) {
      const key = content.slice(0, colonIdx).trim();
      const value = content.slice(colonIdx + 1).trim();

      if (value === '' || value === '|' || value === '>') {
        current[key] = {};
        stack.push({ obj: current[key] as Record<string, unknown>, indent, key });
      } else {
        current[key] = parseValue(value);
        stack.push({ obj: current, indent, key });
      }
    }
  }

  return result;
}

function parseValue(value: string): unknown {
  if (value === 'null' || value === '~' || value === '') return null;
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (/^-?\d+$/.test(value)) return parseInt(value, 10);
  if (/^-?\d*\.\d+$/.test(value)) return parseFloat(value);
  if (
    (value.startsWith('"') && value.endsWith('"')) ||
    (value.startsWith("'") && value.endsWith("'"))
  ) {
    return value.slice(1, -1).replace(/\\n/g, '\n').replace(/\\"/g, '"');
  }
  return value;
}
